import React, { useEffect, useRef, useState } from "react";
import { X, Plus } from "lucide-react";
import api from "../lib/api";

/**
 * Multi-select chip input for product categories.
 * - value: string[]
 * - onChange: (next: string[]) => void
 * - disabled: bool
 * - data-testid: prefix for test ids
 */
export default function CategoryPicker({ value = [], onChange, disabled = false, testid = "categories" }) {
  const [draft, setDraft] = useState("");
  const [suggestions, setSuggestions] = useState([]);
  const [open, setOpen] = useState(false);
  const wrapRef = useRef(null);

  useEffect(() => {
    let mounted = true;
    api.get("/categories").then(({ data }) => {
      if (mounted) {
        // backend now returns {flat, tree}; fall back to array for safety
        const flat = Array.isArray(data) ? data : (data?.flat || []);
        setSuggestions(flat);
      }
    }).catch(() => {});
    return () => { mounted = false; };
  }, []);

  useEffect(() => {
    const onDoc = (e) => { if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, []);

  const add = (raw) => {
    const c = (raw || draft).trim();
    if (!c) return;
    if (value.map((v) => v.toLowerCase()).includes(c.toLowerCase())) {
      setDraft(""); return;
    }
    onChange([...value, c]);
    setDraft("");
  };

  const remove = (c) => onChange(value.filter((v) => v !== c));

  const filtered = (suggestions || [])
    .filter((s) => !value.map((v) => v.toLowerCase()).includes(s.name.toLowerCase()))
    .filter((s) => s.name.toLowerCase().includes(draft.trim().toLowerCase()))
    .slice(0, 8);

  return (
    <div ref={wrapRef} className="relative" data-testid={testid}>
      <div className="brutal-input flex flex-wrap gap-2 min-h-[44px] items-center">
        {value.map((c) => (
          <span key={c} className="brutal-badge !py-1 flex items-center gap-1" data-testid={`${testid}-chip-${c}`}>
            {c}
            {!disabled && (
              <button type="button" onClick={() => remove(c)} className="text-[#D9381E]" aria-label={`Remove ${c}`}>×</button>
            )}
          </span>
        ))}
        {!disabled && (
          <input
            className="flex-1 min-w-[120px] outline-none bg-transparent font-mono text-sm"
            placeholder={value.length ? "Add another…" : "e.g. Stickers, Apparel"}
            value={draft}
            onChange={(e) => { setDraft(e.target.value); setOpen(true); }}
            onFocus={() => setOpen(true)}
            onKeyDown={(e) => {
              if (e.key === "Enter" || e.key === ",") { e.preventDefault(); add(); }
              if (e.key === "Backspace" && !draft && value.length) remove(value[value.length - 1]);
            }}
            data-testid={`${testid}-input`}
          />
        )}
        {!disabled && draft.trim() && (
          <button type="button" onClick={() => add()} className="brutal-badge cursor-pointer" data-testid={`${testid}-add`}>
            <Plus size={10}/> Add
          </button>
        )}
      </div>
      {!disabled && open && (filtered.length > 0) && (
        <div className="absolute z-30 mt-1 left-0 right-0 brutal-card-sm bg-white max-h-56 overflow-auto" data-testid={`${testid}-suggestions`}>
          {filtered.map((s) => (
            <button
              key={s.name} type="button"
              onClick={() => { add(s.name); setOpen(false); }}
              className="w-full text-left px-3 py-2 hover:bg-[#F4F2EC] flex items-center justify-between border-b-2 border-black last:border-b-0"
              data-testid={`${testid}-suggestion-${s.name}`}
            >
              <span className="font-mono text-sm">{s.name}</span>
              <span className="font-mono text-[10px] uppercase tracking-widest text-[#4A4A4A]">{s.count}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
